import React from "react";
import { Download, ArrowRight } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { weeklyHours, goals, recentlyCompleted, userPerformance } from "../data/mockData";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function ProgressPage() {
    return (
        <AppLayout searchPlaceholder="Search analytics, goals...">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Learning Analytics</h1>
                <div className="flex gap-3">
                    <select data-testid="period-select" className="appearance-none bg-ink-700 border border-white/10 rounded-full px-5 py-2.5 text-sm font-medium hover:border-white/20 focus:outline-none">
                        <option>Last 30 days</option>
                        <option>Last 7 days</option>
                        <option>This year</option>
                    </select>
                    <button data-testid="export-report" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold hover:bg-lime-hover transition-all hover:-translate-y-0.5">
                        <Download className="w-4 h-4" /> Export Report
                    </button>
                </div>
            </div>

            {/* Top row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className="lg:col-span-2 bg-ink-700 rounded-3xl border border-white/5 p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="font-display font-bold text-xl">Task Completion</h2>
                        <div className="flex items-center gap-4 text-xs">
                            <span className="flex items-center gap-1.5 text-gray-400"><span className="w-2 h-2 rounded-full bg-grape" /> Completed</span>
                            <span className="flex items-center gap-1.5 text-gray-400"><span className="w-2 h-2 rounded-full bg-lime" /> In Progress</span>
                        </div>
                    </div>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%" minHeight={250}>
                            <BarChart data={weeklyHours} barCategoryGap={12}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis dataKey="day" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                                <Tooltip
                                    cursor={{ fill: "rgba(255,255,255,0.04)" }}
                                    contentStyle={{ background: "#0E0F12", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }}
                                />
                                <Bar dataKey="completed" stackId="a" fill="#7C5CFF" />
                                <Bar dataKey="inProgress" stackId="a" fill="#CFFF50" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Goal Tracking circular */}
                <div className="bg-ink-700 rounded-3xl border border-white/5 p-6 flex flex-col">
                    <h2 className="font-display font-bold text-xl mb-6">Goal Tracking</h2>
                    <div className="flex-1 flex flex-col items-center justify-center text-center">
                        <div className="relative w-44 h-44">
                            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                                <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
                                <circle cx="50" cy="50" r="42" fill="none" stroke="#CFFF50" strokeWidth="8" strokeLinecap="round" strokeDasharray={`${(82 / 100) * 263.9} 263.9`} />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <div className="font-display font-black text-4xl">82%</div>
                                <div className="text-xs text-gray-400 mt-1">Completed</div>
                            </div>
                        </div>
                        <p className="text-sm text-gray-400 mt-6 max-w-xs">Target: 95% course completion rate by end of Q3.</p>
                        <button data-testid="set-new-goal" className="mt-6 w-full px-5 py-3 rounded-full border border-white/15 text-sm font-semibold hover:bg-white/5 transition-colors">
                            Set New Goal
                        </button>
                    </div>
                </div>
            </div>

            {/* Goals + recently completed */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className="lg:col-span-2 bg-ink-700 rounded-3xl border border-white/5 p-6">
                    <div className="flex items-center justify-between mb-5">
                        <h2 className="font-display font-bold text-xl">Active Goals ({goals.length})</h2>
                        <button data-testid="view-all-goals" className="text-sm text-lime font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all">View all <ArrowRight className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="space-y-4">
                        {goals.map((g) => (
                            <div key={g.id} data-testid={`goal-${g.id}`} className="bg-ink-800 rounded-2xl p-5 border border-white/5">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        <div className="font-display font-black text-3xl text-white">{g.progress}%</div>
                                        <div>
                                            <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-bold mb-1 ${g.type === "Daily Habit" ? "bg-lime/15 text-lime" : "bg-grape/15 text-grape"}`}>
                                                {g.type}
                                            </span>
                                            <div className="font-display font-bold">{g.title}</div>
                                            <div className="text-xs text-gray-500 mt-1">{g.description}</div>
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-xs text-gray-500">{g.streak ? "Streak" : "Due in"}</div>
                                        <div className="font-display font-bold text-lg">{g.streak || g.due}</div>
                                    </div>
                                </div>
                                <div className="mt-4 h-1 rounded-full bg-ink-600 overflow-hidden">
                                    <div className="h-full rounded-full bg-gradient-to-r from-grape to-lime" style={{ width: `${g.progress}%` }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bg-ink-700 rounded-3xl border border-white/5 p-6">
                    <h2 className="font-display font-bold text-xl mb-5">Recently Completed</h2>
                    <ul className="space-y-3">
                        {recentlyCompleted.map((rc, i) => (
                            <li key={i} className="flex items-center gap-3">
                                <span className="w-7 h-7 rounded-full bg-grape/20 flex items-center justify-center">
                                    <span className="text-grape">✓</span>
                                </span>
                                <span className="text-sm">{rc}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-5 pt-5 border-t border-white/5">
                        <div className="text-xs text-gray-500 mb-3">Goal Summary</div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between"><span className="text-gray-400">Completed</span><span className="font-bold">12</span></div>
                            <div className="flex justify-between"><span className="text-gray-400">In Progress</span><span className="font-bold">3</span></div>
                            <div className="flex justify-between"><span className="text-gray-400">Success Rate</span><span className="font-bold text-lime">86%</span></div>
                        </div>
                    </div>
                </div>
            </div>

            {/* User Performance */}
            <div className="bg-ink-700 rounded-3xl border border-white/5 p-6">
                <div className="flex items-center justify-between mb-5">
                    <h2 className="font-display font-bold text-xl">User Performance</h2>
                    <button data-testid="view-all-perf" className="text-sm text-lime font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all">View All <ArrowRight className="w-3.5 h-3.5" /></button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="text-xs text-gray-500 uppercase tracking-widest border-b border-white/5">
                                <th className="text-left font-medium pb-3">Student</th>
                                <th className="text-left font-medium pb-3">Course</th>
                                <th className="text-left font-medium pb-3">Progress</th>
                                <th className="text-left font-medium pb-3">Score</th>
                                <th className="text-left font-medium pb-3">Last Active</th>
                            </tr>
                        </thead>
                        <tbody>
                            {userPerformance.map((u, i) => (
                                <tr key={i} className="border-b border-white/5 last:border-b-0">
                                    <td className="py-4">
                                        <div className="flex items-center gap-3">
                                            <img src={u.avatar} alt={u.name} className="w-9 h-9 rounded-full object-cover" />
                                            <span className="font-semibold text-sm">{u.name}</span>
                                        </div>
                                    </td>
                                    <td className="text-sm text-gray-300">{u.course}</td>
                                    <td className="w-48">
                                        <div className="flex items-center gap-3">
                                            <div className="flex-1 h-1.5 rounded-full bg-ink-600 overflow-hidden">
                                                <div className="h-full rounded-full bg-grape" style={{ width: `${u.progress}%` }} />
                                            </div>
                                            <span className="text-xs font-semibold text-grape">{u.progress}%</span>
                                        </div>
                                    </td>
                                    <td className={`text-sm font-bold ${parseFloat(u.score) >= 8 ? "text-lime" : parseFloat(u.score) >= 7 ? "text-yellow-400" : "text-red-400"}`}>{u.score}</td>
                                    <td className="text-sm text-gray-400">{u.lastActive}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </AppLayout>
    );
}
